import React, { useEffect, useState, useCallback, useMemo } from "react";
import ReactPaginate from "react-paginate";
import BooksAndPublicationsItem from "../components/booksAndPublications/BooksAndPublicationsItem";

import { useDispatch, useSelector } from "react-redux";
import {
  getCurrentMenuWisePageConfig,
  getMenuWisePageConfig,
} from "../redux/menu/menuWisePageConfigSlice";
import { getCurrentSelectedMenuID } from "../redux/menu/menuWisePageConfigSlice";

import { gridGenerator } from "../helper/helper";
import { getRowsPerPage } from "../redux/menu/menuSlice";
import Loader from "../components/loader/Loader";
import {
  getAllblogs,
  getBlogByTags,
  getBlogs,
  getPostByType,
} from "../redux/blog/blogSlice";
import SideSearch from "../components/search/SideSearch";
import SideSearchBooksAndPublications from "../components/search/SideSearchBooksAndPublications";
import researchGateImg from "./../assets/images/researchgate.png";
import googlescholarImg from "./../assets/images/googlescholar.png";
import academiaImg from "./../assets/images/academia.png";

/**
 * External Links Configuration
 * Following Open/Closed Principle - easy to extend without modifying component
 */
const EXTERNAL_LINKS = [
  {
    id: "google-scholar",
    href: "https://scholar.google.com/citations?user=pQZkFOoAAAAJ&hl=en",
    label: "Google Scholar",
    icon: googlescholarImg,
    iconClass: "rounded-full",
  },
  {
    id: "academia",
    href: "https://aust.academia.edu/AAlMuyeed",
    label: "Academia",
    icon: academiaImg,
    iconClass: "rounded-full",
  },
  {
    id: "researchgate",
    href: "https://www.researchgate.net/profile/Abdullah_Al-Muyeed",
    label: "ResearchGate",
    icon: researchGateImg,
    iconClass: "",
  },
];

/**
 * Post Type Tabs Configuration
 */
const POST_TYPES = ["publications", "books"];

/**
 * ExternalLinkButton Component
 * Single Responsibility - handles rendering of external link buttons
 */
const ExternalLinkButton = ({ href, label, icon, iconClass }) => (
  <a
    className="flex items-center gap-2 sm:gap-3 bg-indigo-500 hover:bg-indigo-700 
               text-white text-xs sm:text-sm px-3 sm:px-6 py-2 whitespace-nowrap 
               rounded-full transition-all duration-300 hover:shadow-lg"
    href={href}
    target="_blank"
    rel="noopener noreferrer"
  >
    <img 
      src={icon} 
      alt={label} 
      className={`w-5 h-5 sm:w-7 sm:h-7 ${iconClass}`}
    />
    <span className="hidden sm:inline">{label}</span>
  </a>
);

/**
 * PostTypeTab Component
 * Single Responsibility - handles individual tab rendering
 */
const PostTypeTab = ({ item, isSelected, onSelect }) => (
  <li
    className={`
      px-4 sm:px-6 py-2 sm:py-3 rounded-full cursor-pointer capitalize
      text-sm sm:text-base font-medium transition-all duration-300
      ${isSelected 
        ? "bg-indigo-500 text-white shadow-md" 
        : "bg-gray-100 hover:bg-indigo-100 text-gray-700"
      }
    `}
    onClick={(e) => onSelect(e, item)}
  >
    {item}
  </li>
);

/**
 * PageHeader Component
 * Single Responsibility - renders page title with shadow effect
 */
const PageHeader = ({ title, shadowTitle }) => (
  <div className="relative mb-6 sm:mb-8">
    <h2 className="relative z-10 font-extrabold text-2xl sm:text-3xl md:text-4xl lg:text-5xl">
      {title}
    </h2>
    <h2 
      className="font-extrabold text-3xl sm:text-4xl md:text-5xl opacity-10 
                 absolute top-4 sm:top-6 leading-[.6] left-0 z-[1]"
    >
      {shadowTitle}
    </h2>
  </div>
);

const BooksAndPublications = () => {
  const dispatch = useDispatch();
  const currentPageConfig = useSelector(getCurrentMenuWisePageConfig);
  const blogData = useSelector(getAllblogs);
  const currentMenuID = useSelector(getCurrentSelectedMenuID);
  const itemsPerPage = useSelector(getRowsPerPage);

  // State management
  const [currentItems, setCurrentItems] = useState(null);
  const [pageCount, setPageCount] = useState(0);
  const [itemOffset, setItemOffset] = useState(0);
  const [gridGeneratorClassName, setGridGeneratorClassName] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState("publications");

  // Memoized values for performance
  const hasSidebar = useMemo(() => 
    currentPageConfig?.sidebarSearch, 
    [currentPageConfig]
  );

  // Initial data fetch
  useEffect(() => {
    dispatch(getPostByType(selectedItem));
    dispatch(getMenuWisePageConfig(currentMenuID)).then(() =>
      setTimeout(() => setIsLoading(false), 1000)
    );
  }, [currentMenuID, dispatch, selectedItem]);

  // Pagination logic
  useEffect(() => {
    if (blogData?.length > 0) {
      const endOffset = itemOffset + itemsPerPage;
      setCurrentItems(blogData.slice(itemOffset, endOffset));
      setPageCount(Math.ceil(blogData.length / itemsPerPage));
    } else {
      setCurrentItems([]);
      setPageCount(0);
    }
  }, [itemOffset, itemsPerPage, blogData]);

  // Grid class generation
  useEffect(() => {
    setGridGeneratorClassName(gridGenerator(currentPageConfig?.columns));
  }, [currentPageConfig]);

  // Event handlers with useCallback for performance
  const handlePageClick = useCallback((event) => {
    if (blogData?.length > 0) {
      const newOffset = (event.selected * itemsPerPage) % blogData.length;
      setItemOffset(newOffset);
    }
  }, [blogData, itemsPerPage]);

  const handleChangeTags = useCallback((tags) => {
    dispatch(getBlogByTags({ type: selectedItem, tag: tags }));
  }, [dispatch, selectedItem]);

  const handleSelectType = useCallback((e, type) => {
    setSelectedItem(type);
    setItemOffset(0);
    dispatch(getPostByType(type));
  }, [dispatch]);

  return (
    <div className="p-3 sm:p-4 md:p-6 lg:p-8">
      {isLoading && <Loader />}
      
      <PageHeader 
        title={currentPageConfig?.menu_title}
        shadowTitle={currentPageConfig?.shadow_title}
      />

      {/* External Links & Type Selection Section */}
      <div className="w-full max-w-7xl mx-auto my-4 sm:my-6 md:my-8">
        {/* External Links */}
        <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 mb-6 sm:mb-8">
          {EXTERNAL_LINKS.map(link => (
            <ExternalLinkButton key={link.id} {...link} />
          ))}
        </div>

        {/* Post Type Tabs */}
        <ul className="w-full flex items-center justify-center gap-2 sm:gap-4 flex-wrap">
          {POST_TYPES.map((item) => (
            <PostTypeTab
              key={item}
              item={item}
              isSelected={selectedItem === item}
              onSelect={handleSelectType}
            />
          ))}
        </ul>
      </div>

      {/* Main Content Area */}
      <div className="w-full flex flex-col xl:flex-row items-start justify-between gap-4 sm:gap-6 xl:gap-8 mt-6 sm:mt-8">
        {/* Posts Grid */}
        <div className={`w-full ${hasSidebar ? "xl:flex-[4]" : ""} flex flex-col`}>
          {currentItems?.length > 0 ? (
            <div className={gridGeneratorClassName}>
              {currentItems.map((item) => (
                <BooksAndPublicationsItem
                  key={item.id}
                  item={item}
                  currentPageConfig={currentPageConfig}
                  selectedItem={selectedItem}
                />
              ))}
            </div>
          ) : (
            <div className="w-full min-h-[200px] flex items-center justify-center">
              <p className="text-xl sm:text-2xl md:text-3xl font-semibold text-slate-500">
                No Data Found
              </p>
            </div>
          )}

          {/* Pagination */}
          {pageCount > 1 && (
            <ReactPaginate
              breakLabel="..."
              nextLabel=">>"
              onPageChange={handlePageClick}
              pageRangeDisplayed={3}
              marginPagesDisplayed={1}
              pageCount={pageCount}
              previousLabel="<<"
              renderOnZeroPageCount={null}
              className="w-full flex flex-wrap items-center justify-center gap-2 sm:gap-4 md:gap-6 mt-6 sm:mt-8"
              activeClassName="bg-indigo-500 text-white rounded-full py-2"
              pageLinkClassName="border px-3 sm:px-4 py-1 sm:py-2 rounded-full hover:bg-slate-200 
                                text-sm sm:text-base transition-colors duration-200"
              previousLinkClassName="border px-3 sm:px-4 py-1 sm:py-2 rounded-full hover:bg-slate-200 
                                     text-sm sm:text-base transition-colors duration-200"
              nextLinkClassName="border px-3 sm:px-4 py-1 sm:py-2 rounded-full hover:bg-slate-200 
                                text-sm sm:text-base transition-colors duration-200"
              disabledLinkClassName="opacity-50 cursor-not-allowed"
            />
          )}
        </div>

        {/* Sidebar */}
        {hasSidebar && (
          <aside className="w-full xl:flex-1 xl:max-w-[280px] flex flex-col 
                          xl:sticky xl:top-4 order-first xl:order-last">
            <SideSearchBooksAndPublications
              handleChangeTags={handleChangeTags}
              postType={selectedItem}
            />
          </aside>
        )}
      </div>
    </div>
  );
};

export default BooksAndPublications;
